import clsx from 'clsx';
import InterviewActionsCombobox from 'components/comboboxes/InterviewActionsCombobox';
import MutedText from 'components/common/MutedText';
import { HubertButton } from 'components/HubertUI/HubertButton';
import HubertLabel from 'components/HubertUI/HubertLabel';
import { useTemplateId } from 'hooks/useTemplate';
import { Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from 'rtk/query/hooks';
import { selectQuestionIsReadonly, selectQuestionLanguage, selectQuestionMode } from 'rtk/questionEditorSelector';
import { customParametersUpdate } from 'rtk/TemplateSlice';
import { EEventType } from 'types/events.enum';
import { ECreateQuestionMode, useQuestionEditorContext } from '../context/create-question.context';

import { updatePostActions } from 'rtk/Question.Editor.Slice';

const PostQuestionActions = () => {
  const { t } = useTranslation('Questionsv2');
  const dispatch = useAppDispatch();
  const { questionId } = useQuestionEditorContext();
  const mode = useAppSelector((state) => selectQuestionMode(state, questionId));
  const isReadOnly = useAppSelector((state) => selectQuestionIsReadonly(state, questionId));
  const templateId = useTemplateId();
  const questionLanguage = useAppSelector((state) => selectQuestionLanguage(state, questionId));
  const postActions = useAppSelector(
    (state) =>
      state.Template?.present?.c?.parameters?.[questionId]?.post_actions ??
      state.questionEditor.byId[questionId]?.post_actions,
  );

  const updatePostActionHelper = (x: string[]) => {
    if (templateId && mode === ECreateQuestionMode.InTemplateEdit) {
      dispatch(
        customParametersUpdate({
          templateId,
          resource: {
            nodeId: questionId,
            key: 'post_actions',
            value: x,
            language: questionLanguage,
            isDefault: false,
          },
          eventType: EEventType.ADDED,
          description: `User set custom post_actions for ${questionId}.`,
        }),
      );
    } else {
      dispatch(updatePostActions({ questionId, actions: x }));
    }
  };

  return (
    <div
      className={clsx(
        // mode === ECreateQuestionMode.InTemplateEdit && 'hidden',
        'flex flex-col justify-start gap-4',
        'p-1',
      )}
    >
      <div
        className={clsx(
          mode === ECreateQuestionMode.InTemplateEdit && 'hidden',
          'flex flex-row justify-start gap-4',
          'hubert-vertical-center',
        )}
      >
        <HubertLabel className="cursor-pointer text-sm font-medium" htmlFor="create-question-text">
          {t('postActions.label')}
        </HubertLabel>

        <HubertButton
          size="xs"
          onClick={() => {
            const x = [...(postActions || [])];
            x.push('');
            updatePostActionHelper(x);
          }}
        >
          {t('postActions.addAction')}
        </HubertButton>
      </div>
      <div className="">
        <MutedText text={t('postActions.hint')} />
      </div>

      <div>
        {postActions &&
          postActions.map((action: string, index: number) => {
            return (
              <div
                key={`question-post-action-${questionId}-${index}`}
                className={clsx(
                  'flex flex-row justify-between',
                  'hubert-vertical-center p-2 pr-4',
                  'hover:bg-primary-50',
                  'rounded-md',
                )}
              >
                <InterviewActionsCombobox
                  id={`create-question-interview-post-action-${index}`}
                  isMulti={false}
                  onValueChange={(value) => {
                    const x = [...(postActions || [])];
                    x[index] = value as string;
                    updatePostActionHelper(x);
                  }}
                  disabled={isReadOnly}
                  value={action}
                />
                <Trash2
                  className="text-danger-400 cursor-pointer"
                  onClick={() => {
                    const x = [...(postActions || [])];
                    x.splice(index, 1);
                    updatePostActionHelper(x);
                  }}
                />
              </div>
            );
          })}
      </div>
    </div>
  );
};

export default PostQuestionActions;
